<template>
    <div v-if="existingTransaction" class="space-y-4 p-2">
        <UAlert
            :title="`Endrer bilag ${existingTransaction.transactionName}`"
            icon="i-ph-pencil"
            color="warning"
            class="text-default"
            variant="subtle"
            data-cy="transaction-edit-header"
            :ui="{
                icon: 'text-warning'
            }"
        />
        <div
            v-if="notDeletedExistingAttachments.length"
            class="flex flex-col gap-2"
        >
            <div>Filer</div>
            <div class="flex gap-2 rounded-md flex-wrap">
                <document-meta-badge
                    v-for="existingAttachment in notDeletedExistingAttachments"
                    :key="existingAttachment.uuid"
                    :document-meta="existingAttachment"
                    has-delete
                    @on-click-delete="handleDeleteAttachment"
                />
            </div>
        </div>
    </div>
    <create-transaction />
    <div class="mx-2 p-2 border rounded">
        <div class="flex gap-2 items-center relative">
            <UButton
                color="neutral"
                variant="outline"
                :data-cy="TransactionManagerCy.ButtonCancel"
                :disabled="isResetDisabled"
                @click="handleClickClear"
            >
                Avbryt
            </UButton>
            <div class="grow"/>
            <div class="flex-wrap">
                <div v-for="error in entriesValidationResult.errors" :key="error" class="text-error flex items-center gap-1">
                    <i class="i-ph-warning-fill block text-error text-lg"/>
                    {{ cachedT(`create-transaction.validation.errors.${error}`) }}
                </div>
                <div v-if="duplicateInvoiceLineUuids.length" class="text-warning flex items-center gap-1">
                    <i class="i-ph-warning-fill block text-warning text-lg"/>
                    Fakturanummer er allerede registrert fra denne kunden/leverandøren
                </div>
            </div>
            <drop-down-button :options="buttons"/>
            <create-transaction-submit
                :is-editing="isEdit"
                :is-loading="isLoading"
                :is-edit-submit-disabled="!isEditDirty"
                @submit="handleClickSubmit"
            />
        </div>
    </div>
</template>

<script setup lang="ts">

import CreateTransaction from '@/transaction/create-transaction/CreateTransaction.vue'
import {useEditTransactionCreator} from '@/transaction/edit-transaction/EditTransaction.service'
import {computed, onMounted, onUnmounted, ref} from 'vue'
import emitter, {useEmitter} from '@/services/infrastructure/emitter'
import {ORG_SELECTED} from '@/types/events'
import {entriesValidationResult, isTransactionEntryLineEmpty, useTransactionCreator} from '@/transaction/create-transaction/TransactionCreator.service'
import {duplicateInvoiceLineUuids} from '@/transaction/create-transaction/duplicateInvoiceWarnings'
import DropDownButton from '@/components/dropdownButton/DropDownButton.vue'
import CreateTransactionSubmit from '@/transaction/create-transaction/CreateTransactionSubmit.vue'
import {defaultTransactionDraftName, draftUuid, useTransactionDrafts} from '@/transaction-journal/transaction.draft.service'
import {hasSelectedOrg, orgSelectionComplete} from '@/tenants/org.service'
import {notifyError, notifySuccess} from '@/services/infrastructure/notification.service'
import {useCachedT} from '@/services/useCachedI18n'
import {DropDownButtonOption} from '@/components/dropdownButton/DropDownButtonOption.type'
import {entryLinesToTransactionLines} from '@/transaction/transaction.mapper'
import {Draft, DRAFT_TYPE} from '@/draft/drafts.service'
import {TRANSACTION_DRAFT_CREATED} from '@/types/transaction/TransactionEvent'
import {registerIncomingDocuments} from '@/services/document.service'
import DocumentMetaBadge from '@/document/DocumentMetaBadge.vue'
import {TransactionManagerCy} from '@/transaction/TransactionManagerCy'
import {v4 as uuidv4} from 'uuid'
import {usePanelManager} from '@tritt/multipanel'
import {CREATE_TRANSACTION} from '@/transaction/create-transaction'

const isLoading = ref(false)

const {reset: resetCreate, isDirty: isCreateDirty, saveTransactionFromState, transactionEntryLines, attachments} = useTransactionCreator()
const {reset: resetEdit, isDirty: isEditDirty, existingTransaction, patchTransaction, existingAttachments, closeSlideIn} = useEditTransactionCreator()
const {saveDraft, getTransactionDraft, updateDraft} = useTransactionDrafts()
const {cachedT} = useCachedT()
const {onEvents, cleanUp} = useEmitter()
const {contentExistInPanels} = usePanelManager()

const notDeletedExistingAttachments = computed(() => existingAttachments.value.filter(att => !att.isDeleted))

const isEdit = computed(() => Boolean(existingTransaction.value))

const isResetDisabled = computed(() => {
    if (isEdit.value) {
        // should be able to clear an edit.
        return false
    }
    return !isCreateDirty.value
})

const buttons = computed<DropDownButtonOption[]>(() => {
    const options: DropDownButtonOption[] = []
    if (draftUuid.value) {
        options.push({
            label: 'Oppdater kladd',
            onClick: () => updateTransactionDraft(),
            disabled: !draftUuid.value || isTransactionEntryLineEmpty.value,
            dataCy: 'update-draft-button',
            classList: ['btn-outline'],
        })
    }

    options.push({
        label: 'Lagre kladd',
        onClick: () => saveTransactionDraft(),
        disabled: isTransactionEntryLineEmpty.value,
        dataCy: 'save-draft-button',
        classList: ['btn-outline'],
    })

    return options
})

onMounted(async () => {
    onEvents([ORG_SELECTED], handleClickClear)
    await orgSelectionComplete
    if (!hasSelectedOrg()) {
        console.error('Accessing the accounting page without selected org. Should not be possible, this is a bug!')
    }
})

onUnmounted(() => {
    cleanUp()
    if (!contentExistInPanels(CREATE_TRANSACTION.componentName)) {
        resetCreate()
        resetEdit()
    }
})

//TODO: should encapsulate draft logic inside draft-service
const saveTransactionDraft = async () => {
    const lines = entryLinesToTransactionLines(transactionEntryLines.value)
    const linesWithUuid = lines.map(line => ({...line, uuid: uuidv4()}))
    await registerIncomingDocuments(attachments.value)

    const transactionDraft: Draft = {
        name: defaultTransactionDraftName(),
        type: DRAFT_TYPE.TRANSACTION,
        editTransactionUuid: existingTransaction.value?.uuid,
        content: {linesWithUuid, documentsMetas: attachments.value.map(file => file.uuid)}
    }

    const response = await saveDraft(transactionDraft)

    if (response.uuid) {
        emitter.emit(TRANSACTION_DRAFT_CREATED)
        draftUuid.value = response.uuid
        notifySuccess({title: 'Opprettet', description: `Kladden ${transactionDraft.name} ble lagret.`})
    }
}

const updateTransactionDraft = async () => {
    const uuid = draftUuid.value
    if (!uuid) return false

    const name = getTransactionDraft(uuid)!.name

    const lines = entryLinesToTransactionLines(transactionEntryLines.value)
    const linesWithUuid = lines.map(line => ({...line, uuid: uuidv4()}))

    const transactionDraft: Draft = {
        uuid: draftUuid.value,
        name,
        type: DRAFT_TYPE.TRANSACTION,
        editTransactionUuid: uuid,
        content: {linesWithUuid, documentsMetas: attachments.value.map(file => file.uuid)}
    }
    const response = await updateDraft(transactionDraft)
    if (response.isRight()) {
        emitter.emit(TRANSACTION_DRAFT_CREATED)
        notifySuccess({title: 'Oppdatert', description: `Kladden ${transactionDraft.name} ble oppdatert.`})
    }
}

const handleClickClear = () => {
    if (!isEdit.value) {
        resetCreate()
    } else {
        resetEdit()
        closeSlideIn()
    }
}

const handleClickSubmit = async (e: { payNow: boolean }) => {
    if (!isEdit.value) {
        await handleSave(e.payNow)
    } else {
        await handleUpdate()
    }
}

const handleSave = async (payNow: boolean) => {
    if (isEdit.value) {
        throw new Error('Unsupported create')
    }
    isLoading.value = true
    try {
        await registerIncomingDocuments(attachments.value)
        await saveTransactionFromState(payNow)
    } catch (error) {
        notifyError({title: 'Feilet', description: {descriptionMessage: 'Klarte ikke å opprette transaksjon', error}})
    } finally {
        isLoading.value = false
    }
}

const handleUpdate = async () => {
    if (!isEdit.value) {
        throw new Error('Unsupported update')
    }
    isLoading.value = true
    try {
        await registerIncomingDocuments(attachments.value)
        await patchTransaction()
    } catch (error) {
        notifyError({title: 'Feilet', description: {descriptionMessage: 'Klarte ikke å oppdatere transaksjon', error}})
    } finally {
        isLoading.value = false
        closeSlideIn()
    }
}

const handleDeleteAttachment = (uuid: string) => {
    existingAttachments.value = existingAttachments.value.map(attachment => {
        if (attachment.uuid === uuid) {
            return {...attachment, isDeleted: true}
        }
        return attachment
    })
}


</script>

<style scoped>

</style>